/**
 * Human-readable inline error copy for the auth pages (login/signup).
 * Replaces the raw Firebase toasts ("Firebase: Error (auth/invalid-credential)")
 * with persistent, actionable inline notices rendered inside the card.
 * Event taxonomy for these pages lives in js/cellcog/TRACKING.md.
 */

// Errors that are user CHOICES, not failures — show no error UI, track nothing.
const SILENT_CODES = new Set([
  'auth/popup-closed-by-user',
  'auth/cancelled-popup-request',
]);

export function isSilentAuthError(error) {
  return SILENT_CODES.has(error?.code);
}

/**
 * @param {Error} error - Firebase auth error (error.code drives the copy)
 * @param {string} page - 'login' | 'signup'
 * @param {string} [email] - the email the user typed, carried into cross-page links
 * @returns {{text: string, links: Array<{label: string, to?: string, action?: 'google'}>}|null}
 *   Links with `to` render as router links; links with `action` render as
 *   in-card actions the auth page wires to its own handler (e.g. the Google
 *   sign-in fallback when an ad blocker kills the email path).
 */
export function getAuthErrorNotice(error, page, email) {
  if (isSilentAuthError(error)) return null;
  const code = error?.code || 'unknown';
  const emailQ = email ? `?email=${encodeURIComponent(email)}` : '';

  switch (code) {
    // Firebase email-enumeration protection makes "no such account" and "wrong
    // password" indistinguishable on login — so the notice offers BOTH paths.
    case 'auth/invalid-credential':
    case 'auth/user-not-found':
    case 'auth/wrong-password':
      return {
        text: "That email and password don't match.",
        links: [
          { label: 'New here? Create an account →', to: `/signup${emailQ}` },
          { label: 'Reset password', to: '/forgot-password' },
        ],
      };
    case 'auth/email-already-in-use':
      return {
        text: 'You already have an account with this email.',
        links: [{ label: 'Log in →', to: `/login${emailQ}` }],
      };
    case 'auth/too-many-requests':
      return {
        text: 'Too many attempts — wait a minute and try again, or reset your password.',
        links: [{ label: 'Reset password', to: '/forgot-password' }],
      };
    case 'auth/network-request-failed':
      // Google sign-in rides a different network path than the REST auth
      // calls, so it's the reliable escape hatch when a blocker bites —
      // rendered as a one-click action inside the notice (CEL-1115).
      return {
        text: "Couldn't reach the sign-in service — this is usually an ad blocker or privacy extension. Pause it for this site and retry, or:",
        links: [{ label: 'Continue with Google →', action: 'google' }],
      };
    case 'auth/popup-blocked':
      // Near-dead path since CEL-1086: AuthContext catches popup-blocked and
      // falls back to signInWithRedirect, so this copy only surfaces if the
      // redirect itself failed to start.
      return {
        text: "Your browser blocked the Google window and the redirect fallback didn't start — allow popups for this site, or use email instead.",
        links: [],
      };
    case 'auth/invalid-email':
      return {
        text: "That email address doesn't look right — double-check it.",
        links: [],
      };
    case 'auth/weak-password':
      return {
        text: 'Password needs at least 6 characters.',
        links: [],
      };
    // Server-side signup refusals (Oct 1 2026, AuthContext.createSessionCookie throws these codes).
    case 'cellcog/signup_disposable_email':
      return {
        text: 'Please use a permanent email address. Temporary inbox services are not accepted.',
        links: [{ label: 'Continue with Google →', action: 'google' }],
      };
    case 'cellcog/signup_rate_limited':
      return {
        text: 'Too many new accounts were created from this network today. Try again tomorrow, or:',
        links: [{ label: 'Continue with Google →', action: 'google' }],
      };
    case 'cellcog/signup_domain_blocked':
      // Neutral on purpose (Nitish, Oct 4 2026): never name which rule fired (domain, exact email or IP),
      // and no contact link (a link hands abusers a form to spam).
      return {
        text: 'Account creation has been blocked. If you think this is a mistake, contact us.',
        links: [],
      };
    default:
      return {
        text: 'Something went wrong on our side — try again in a moment.',
        links: [],
      };
  }
}
